import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULES, EVENT_STATUS, EVENT_CATEGORIES, frDate, eur } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, Info, Wallet, Boxes, Truck, ListOrdered, MapPin, Plus, Trash2, Save, X,
  CheckCircle2, Users, CalendarDays, Sparkles, AlertTriangle, ListChecks,
} from "lucide-react";
import DJModule from "@/pages/DJModule";
import PhotoboothModule from "@/pages/PhotoboothModule";
import { useUnsavedGuard } from "@/context/UnsavedChanges";
import ConfirmDialog from "@/components/ConfirmDialog";
import EventMap from "@/components/event/EventMap";
import { PaymentsTab, LogisticsTab, StepsTab, fmtDateTime } from "@/components/event/EventTabs";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [form, setForm] = useState(null);
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [modTab, setModTab] = useState(null);
  const [venue, setVenue] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [cfEdits, setCfEdits] = useState({});
  const [addModOpen, setAddModOpen] = useState(false);
  const [modToRemove, setModToRemove] = useState(null);
  const { org } = useAuth();

  const savedSnapRef = useRef(null);
  const snapOf = (f, st) => JSON.stringify([f?.title, f?.category, f?.status, f?.date, f?.end_date, f?.load_at, f?.delivery_at, f?.pickup_at, f?.location, f?.guests, f?.budget, f?.notes, st]);

  const load = () => api.get(`/events/${id}`).then(({ data }) => {
    setEv(data);
    const f = {
      title: data.title || "", category: data.category || "Mariage", status: data.status || "prospect",
      date: data.date || "", end_date: data.end_date || "", location: data.location || "",
      load_at: data.load_at || "", delivery_at: data.delivery_at || "", pickup_at: data.pickup_at || "",
      guests: data.guests ?? "", budget: data.budget ?? "", notes: data.notes || "",
    };
    setForm(f);
    setSteps(data.steps || []);
    const edits = {};
    (data.quotes || []).forEach((q) => {
      (q.lines || []).forEach((l) => {
        if ((l.custom_fields || []).length && l.uid) {
          edits[q.id] = edits[q.id] || {};
          const sel = {};
          (l.custom_fields || []).forEach((cf) => {
            const found = (l.custom_selections || []).find((s) => s.label === cf.label);
            sel[cf.label] = found ? found.value : "";
          });
          edits[q.id][l.uid] = sel;
        }
      });
    });
    setCfEdits(edits);
    savedSnapRef.current = snapOf(f, data.steps || []);
    if (!modTab && (data.modules || []).length) setModTab(data.modules[0]);
    if (data.venue_id) api.get(`/venues/${data.venue_id}`).then(({ data: v }) => setVenue(v)).catch(() => setVenue(null));
    else setVenue(null);
  }).catch((e) => { toast.error(apiError(e.response?.data?.detail) || "Événement introuvable"); navigate("/app/calendrier"); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const dirty = !!form && savedSnapRef.current !== null && snapOf(form, steps) !== savedSnapRef.current;
  useUnsavedGuard(dirty);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const buildPayload = (extra = {}) => ({
    title: form.title, contact_id: ev.contact_id || null, venue_id: ev.venue_id || null,
    category: form.category, status: form.status, date: form.date, end_date: form.end_date || null,
    load_at: form.load_at || null, delivery_at: form.delivery_at || null, pickup_at: form.pickup_at || null,
    location: form.location, fallback_location: ev.fallback_location || "",
    location_coords: ev.location_coords || null,
    guests: form.guests === "" ? null : parseInt(form.guests), budget: form.budget === "" ? null : parseFloat(form.budget),
    notes: form.notes, steps, modules: ev.modules || [], ...extra,
  });

  const saveInfos = async () => {
    setSaving(true);
    try { await api.put(`/events/${id}`, buildPayload()); toast.success("Événement enregistré"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };
  const addModule = async (key) => {
    const updated = [...(ev.modules || []), key];
    try {
      await api.put(`/events/${id}`, buildPayload({ modules: updated }));
      toast.success("Module ajouté");
      setModTab(key);
      setAddModOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const removeModule = async () => {
    const key = modToRemove;
    if (!key) return;
    const updated = (ev.modules || []).filter((m) => m !== key);
    try {
      await api.put(`/events/${id}`, buildPayload({ modules: updated }));
      toast.success("Module retiré");
      if (modTab === key) setModTab(updated[0] || null);
      setModToRemove(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const saveSteps = async () => {
    setSaving(true);
    try { await api.put(`/events/${id}`, buildPayload({ steps })); toast.success("Déroulé enregistré"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };
  const complete = async () => {
    try { await api.post(`/events/${id}/complete`); toast.success("Événement clôturé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/events/${id}`); toast.success("Événement supprimé"); setDelOpen(false); navigate("/app/calendrier"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openPdf = (kind, label) => async () => {
    try { const { data } = await api.get(`/events/${id}/${kind}-pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch { toast.error(`Impossible de générer le ${label}`); }
  };
  const loadingPdf = openPdf("loading", "PDF de chargement");
  const returnPdf = openPdf("return", "PDF de retour");
  const setCfField = (qid, uid, label, value) => setCfEdits((p) => ({ ...p, [qid]: { ...(p[qid] || {}), [uid]: { ...((p[qid] || {})[uid] || {}), [label]: value } } }));
  const saveCf = async (qid) => {
    const q = (ev.quotes || []).find((x) => x.id === qid);
    if (!q) return;
    const selections = (q.lines || []).filter((l) => (l.custom_fields || []).length && l.uid).map((l) => ({
      uid: l.uid,
      custom_selections: (l.custom_fields || []).map((cf) => ({ label: cf.label, value: (cfEdits[qid]?.[l.uid]?.[cf.label]) || "" })),
    }));
    try { await api.put(`/quotes/${qid}/custom-selections`, { selections }); toast.success("Champs enregistrés"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const sendBalance = async () => {
    const bq = (ev.quotes || []).find((q) => !["refuse", "expire", "brouillon", "paye"].includes(q.status) && !q.balance_paid && (q.balance || 0) > 0);
    if (!bq) return toast.error("Aucun solde à encaisser.");
    try { await api.post(`/quotes/${bq.id}/send-balance-link`); toast.success("Lien de paiement du solde envoyé au client par email"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Envoi impossible"); }
  };

  const addStep = () => setSteps((s) => [...s, { id: uid(), label: "", date: form?.date || "", time: "", location: "", outdoor: false, fallback_location: "" }]);
  const updStep = (i, k, v) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, [k]: v } : st)));
  const delStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));

  if (!ev || !form) return <p className="text-muted-foreground" data-testid="event-loading">Chargement…</p>;
  const mods = ev.modules || [];
  const availableMods = MODULES.filter((m) => (org?.modules || []).includes(m.key) && !(org?.access?.modules_soon || []).includes(m.key) && !mods.includes(m.key));
  const cfQuotes = (ev.quotes || [])
    .map((q) => ({ q, lines: (q.lines || []).filter((l) => (l.custom_fields || []).length && l.uid) }))
    .filter((x) => x.lines.length);
  const fin = (ev.quotes || []).reduce((a, q) => {
    if (["refuse", "expire", "brouillon"].includes(q.status)) return a;
    const total = q.total || 0;
    const received = q.status === "paye" ? total : (q.deposit_paid ? (q.deposit || 0) : 0) + (q.balance_paid ? (q.balance || 0) : 0);
    a.total += total; a.received += received; return a;
  }, { total: 0, received: 0 });
  fin.due = Math.max(0, fin.total - fin.received);
  const balanceDue = (ev.quotes || []).some((q) => !["refuse", "expire", "brouillon", "paye"].includes(q.status) && !q.balance_paid && (q.balance || 0) > 0);
  const reminderQuotes = (ev.quotes || []).filter((q) => q.last_balance_reminder_at);
  const lastReminderAt = reminderQuotes.map((q) => q.last_balance_reminder_at).sort().slice(-1)[0];
  const reminderCount = reminderQuotes.reduce((a, q) => a + (q.balance_reminder_count || 0), 0);

  return (
    <div className="space-y-5" data-testid="event-detail">
      {dirty && (
        <div data-testid="event-unsaved-banner" className="sticky top-0 z-30 flex items-center justify-between gap-3 rounded-xl border border-[#FFD500]/40 bg-[#1a1710] px-4 py-2.5 text-sm text-[#FFD500] shadow-lg">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />Modifications non enregistrées — cliquez sur « Enregistrer » avant de quitter.</span>
          <Button data-testid="event-unsaved-save" onClick={saveInfos} disabled={saving} className="h-8 shrink-0 rounded-full bg-[#FF6A00] px-4 text-xs font-semibold text-white hover:bg-[#E65F00]"><Save className="mr-1 h-3.5 w-3.5" />Enregistrer</Button>
        </div>
      )}
      <button onClick={() => navigate("/app/calendrier")} data-testid="event-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" />Retour aux événements</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">{ev.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge status={ev.status} />
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{frDate(ev.date)}</span>
            {ev.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>}
            {ev.contact?.name && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{ev.contact.name}</span>}
          </div>
          {ev.last_updated_at && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="ev-last-update">
              Dernière mise à jour le {fmtDateTime(ev.last_updated_at)}{ev.last_updated_by ? ` par ${ev.last_updated_by}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ev.status !== "realise" && ev.status !== "annule" && ev.status !== "refuse" && (
            <Button data-testid="event-complete" onClick={complete} className="rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-500"><CheckCircle2 className="mr-1.5 h-4 w-4" />Marquer réalisé</Button>
          )}
          <Button data-testid="event-delete" onClick={() => setDelOpen(true)} variant="outline" className="rounded-full border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300"><Trash2 className="mr-1.5 h-4 w-4" />Supprimer</Button>
        </div>
      </div>

      {ev.needs_return_check && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-500" data-testid="event-return-warn">
          <AlertTriangle className="mt-0.5 h-4 w-4" />{ev.pending_returns} article(s) à rendre — validez l'état des lieux du matériel avant de clôturer.
        </div>
      )}

      <Tabs defaultValue="infos" className="w-full">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 rounded-xl bg-muted p-1">
          <TabsTrigger value="infos" data-testid="tab-infos" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><Info className="mr-1.5 h-4 w-4" />Infos</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-paiements" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><Wallet className="mr-1.5 h-4 w-4" />Paiements</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><Boxes className="mr-1.5 h-4 w-4" />Modules{mods.length ? ` (${mods.length})` : ""}</TabsTrigger>
          <TabsTrigger value="logistique" data-testid="tab-logistique" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><Truck className="mr-1.5 h-4 w-4" />Logistique</TabsTrigger>
          {cfQuotes.length > 0 && (
            <TabsTrigger value="champs" data-testid="tab-champs" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><ListChecks className="mr-1.5 h-4 w-4" />Champs</TabsTrigger>
          )}
          <TabsTrigger value="deroule" data-testid="tab-deroule" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><ListOrdered className="mr-1.5 h-4 w-4" />Déroulé</TabsTrigger>
          <TabsTrigger value="carte" data-testid="tab-carte" className="rounded-lg data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><MapPin className="mr-1.5 h-4 w-4" />Carte</TabsTrigger>
        </TabsList>

        {/* INFOS */}
        <TabsContent value="infos" className="mt-5">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Titre</Label><Input data-testid="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
              <div><Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="ev-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{EVENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="ev-status" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EVENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Date</Label><Input data-testid="ev-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
              <div><Label>Date de fin (optionnel)</Label><Input data-testid="ev-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
              <div className="sm:col-span-2 mt-1 grid gap-4 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
                <div className="sm:col-span-3 -mb-1 flex items-center gap-1.5 text-sm font-medium text-foreground"><Truck className="h-4 w-4 text-[#FF6A00]" />Logistique</div>
                <div><Label>Jour de chargement</Label><Input data-testid="ev-load-at" type="datetime-local" value={form.load_at || ""} onChange={(e) => setForm({ ...form, load_at: e.target.value })} className="mt-1" /></div>
                <div><Label>Jour de livraison</Label><Input data-testid="ev-delivery-at" type="datetime-local" value={form.delivery_at || ""} onChange={(e) => setForm({ ...form, delivery_at: e.target.value })} className="mt-1" /></div>
                <div><Label>Jour de récupération</Label><Input data-testid="ev-pickup-at" type="datetime-local" value={form.pickup_at || ""} onChange={(e) => setForm({ ...form, pickup_at: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Lieu</Label><Input data-testid="ev-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Invités</Label><Input data-testid="ev-guests" type="number" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-1" /></div>
                <div><Label>Budget (€)</Label><Input data-testid="ev-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="sm:col-span-2"><Label>Notes internes</Label><Textarea data-testid="ev-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <Button data-testid="ev-save-infos" onClick={saveInfos} disabled={saving} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>
              <button data-testid="ev-delete" onClick={() => setDelOpen(true)} className="text-sm text-muted-foreground hover:text-red-500">Supprimer l'événement</button>
            </div>
          </div>
        </TabsContent>

        {/* PAIEMENTS */}
        <TabsContent value="paiements" className="mt-5">
          <PaymentsTab eventId={id} quotes={ev.quotes} fin={fin} balanceDue={balanceDue} onSendBalance={sendBalance}
            lastReminderAt={lastReminderAt} reminderCount={reminderCount} />
        </TabsContent>

        {/* MODULES */}
        <TabsContent value="modules" className="mt-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">{mods.length ? `${mods.length} module${mods.length > 1 ? "s" : ""} activé${mods.length > 1 ? "s" : ""}` : "Aucun module activé"}</p>
            <Button data-testid="ev-add-module-btn" onClick={() => setAddModOpen(true)} className="shrink-0 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1.5 h-4 w-4" />Ajouter un module</Button>
          </div>
          {!mods.length ? (
            <p className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="ev-modules-empty">Aucun module activé pour cette prestation. Cliquez sur « Ajouter un module » ci-dessus, ou activez-en d'autres dans les Paramètres.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2" data-testid="ev-module-nav">
                {mods.map((k) => {
                  const m = MODULES.find((x) => x.key === k);
                  const Icon = m?.icon || Sparkles;
                  const active = modTab === k;
                  return (
                    <div key={k} className={`inline-flex items-center gap-1 rounded-full border pl-3 pr-1.5 py-1.5 text-sm transition-colors ${active ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      <button data-testid={`ev-modtab-${k}`} onClick={() => setModTab(k)} className="inline-flex items-center gap-1.5">
                        <Icon className="h-4 w-4" />{m?.label || k}
                      </button>
                      <button data-testid={`ev-modremove-${k}`} onClick={() => setModToRemove(k)} title="Retirer ce module" className="ml-0.5 rounded-full p-0.5 text-current/60 hover:bg-red-500/15 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  );
                })}
              </div>
              <div data-testid="ev-module-content">
                {modTab === "dj" ? <DJModule embeddedEventId={id} />
                  : modTab === "photobooth" ? <PhotoboothModule embeddedEventId={id} />
                  : (
                    <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center">
                      <Sparkles className="mx-auto h-9 w-9 text-[#FFD500]" />
                      <p className="mt-3 font-display text-lg font-semibold text-foreground">{MODULES.find((x) => x.key === modTab)?.label || "Module"}</p>
                      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Fiche métier bientôt disponible pour ce module.</p>
                    </div>
                  )}
              </div>
            </div>
          )}
          <Dialog open={addModOpen} onOpenChange={setAddModOpen}>
            <DialogContent data-testid="ev-add-module-dialog">
              <DialogHeader>
                <DialogTitle>Ajouter un module</DialogTitle>
                <DialogDescription>Sélectionnez un module à activer pour cet événement.</DialogDescription>
              </DialogHeader>
              {availableMods.length ? (
                <div className="grid grid-cols-2 gap-2">
                  {availableMods.map((m) => {
                    const Icon = m.icon || Sparkles;
                    return (
                      <button key={m.key} data-testid={`ev-add-mod-${m.key}`} onClick={() => addModule(m.key)}
                        className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:border-[#FF6A00] hover:bg-[#FF6A00]/5">
                        <Icon className="h-4 w-4 shrink-0 text-[#FF6A00]" />{m.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="ev-add-mod-none">
                  {(org?.modules || []).length ? "Tous les modules disponibles sont déjà activés pour cet événement." : "Aucun module n'est activé pour votre organisation. Activez-les dans Paramètres › Modules."}
                </p>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* LOGISTIQUE */}
        <TabsContent value="logistique" className="mt-5">
          <LogisticsTab eventId={id} quotes={ev.quotes} reservations={ev.reservations} onLoadingPdf={loadingPdf} onReturnPdf={returnPdf} />
        </TabsContent>

        {/* CHAMPS PERSONNALISÉS */}
        <TabsContent value="champs" className="mt-5">
          <p className="mb-3 text-sm text-muted-foreground">Renseignez les choix des prestations (ex. « Qui mixe ? ») — ils apparaîtront sur le devis PDF.</p>
          {cfQuotes.length ? (
            <div className="space-y-4" data-testid="ev-cf-list">
              {cfQuotes.map(({ q, lines }) => (
                <div key={q.id} className="rounded-2xl border border-border bg-card p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="font-medium text-foreground">{q.number || q.title || "Devis"}</p>
                    <Button data-testid={`ev-cf-save-${q.id}`} onClick={() => saveCf(q.id)} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>
                  </div>
                  <div className="space-y-3">
                    {lines.map((l) => (
                      <div key={l.uid} className="rounded-xl border border-border bg-muted/30 p-4" data-testid={`ev-cf-line-${l.uid}`}>
                        <p className="mb-2 text-sm font-medium text-foreground">{l.name}</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {(l.custom_fields || []).map((cf, k) => (
                            <div key={cf.id || k}>
                              <Label className="text-xs text-muted-foreground">{cf.label}</Label>
                              <Select value={cfEdits[q.id]?.[l.uid]?.[cf.label] || ""} onValueChange={(v) => setCfField(q.id, l.uid, cf.label, v)}>
                                <SelectTrigger data-testid={`ev-cf-${q.id}-${l.uid}-${k}`} className="mt-1"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                <SelectContent>{(cf.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="ev-cf-empty">Aucun champ personnalisé sur les devis de cet événement.</p>}
        </TabsContent>

        {/* DÉROULÉ */}
        <TabsContent value="deroule" className="mt-5">
          <StepsTab steps={steps} onChange={updStep} onDelete={delStep} onAdd={addStep} onSave={saveSteps} saving={saving} />
        </TabsContent>

        {/* CARTE */}
        <TabsContent value="carte" className="mt-5">
          <EventMap coords={ev.location_coords} address={ev.location} venue={venue} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={delOpen} onOpenChange={setDelOpen}
        title="Supprimer cet événement ?"
        description={ev ? `« ${ev.title} » et son déroulé seront définitivement supprimés. Les devis liés ne sont pas supprimés. Cette action est irréversible.` : ""}
        onConfirm={remove} testid="del-event-dialog"
      />
      <ConfirmDialog
        open={!!modToRemove} onOpenChange={(v) => !v && setModToRemove(null)}
        title="Retirer ce module ?"
        description={modToRemove ? `Le module « ${MODULES.find((x) => x.key === modToRemove)?.label || modToRemove} » sera retiré de cet événement. Les fiches associées ne sont pas supprimées et réapparaîtront si vous le réactivez.` : ""}
        onConfirm={removeModule} testid="remove-module-dialog"
      />
    </div>
  );
}
